// Pharmascience — sticky topic action toolbar.
//
// Moves LearnDash's own `.ld-content-actions` row (Previous / Mark complete /
// Next) into the toolbar shell emitted by learndash/ld30/topic.php, and fills
// the Mark-complete button in proportion to reading progress.
//
// 🔴 MOVE, NEVER REBUILD. The mark-complete control is a POST form carrying
// LearnDash's `sfwd_mark_complete` nonce. This module relocates the LIVE node
// with appendChild — no innerHTML, no cloneNode, no re-created inputs — so the
// form, its nonce, its action and LD's own listeners survive intact. Cloning
// would duplicate the nonce field and re-rendering would forge it; both are
// forbidden by the port invariant (presentation only, behaviour never).
//
// Degradation: with JS off, nothing moves. LD's row stays inline at the end of
// the topic and completion still works, because it is a plain form POST. The
// empty toolbar shell collapses via `:empty` in SCSS.

export function initTopicToolbar() {
  const bar = document.querySelector('[data-bnrmp-topicbar]');
  if (!bar) return;

  const inner = bar.querySelector('.bnrmp-topicbar__inner');
  const actions = document.querySelector('.ld-content-actions');
  if (!inner || !actions) return; // LD markup absent/renamed -> leave shell empty

  // The move. Reparenting preserves the node identity, so the nonce input and
  // every LD-bound listener come along with it.
  inner.appendChild(actions);
  bar.classList.add('is-ready');

  // ── Dress LD's controls in the brand button component ───────────────────
  //
  // Operator ruling 2026-08-20 (c): "they should use the same design token of
  // original buttons, we don't need to hand roll the buttons style."
  //
  // The previous round re-declared the button geometry in SCSS with
  // `!important` to out-rank `_learndash.scss`. That was a second copy of
  // `.bnrmp-btn` — and still wrong, because it never named `font-family`
  // (`.bnrmp-btn` declares none and inherits, while LD forces `--font-ui`), so
  // the label kept LD's font no matter how many other properties were copied.
  //
  // Adding the real CLASSES is both smaller and self-maintaining: retone
  // `.bnrmp-btn` and these follow. `classList.add` only ever ADDS — LD's own
  // classes stay on the node, so LD's JS and any selector it relies on keep
  // matching, and nothing here rewrites an attribute, a value or a form.
  //
  // Prev/Next map to Secondary and Mark-complete to Primary: Mark-complete is
  // the primary action on a topic, and three filled buttons would flatten that.
  actions.querySelectorAll('.ld-content-action .ld-button').forEach((el) => {
    el.classList.add('bnrmp-btn', 'bnrmp-btn--outline');
  });

  // ── Group Mark-complete WITH Next, using the design's own wrapper ────────
  //
  // Operator report: "once mark it as complete, the middle cell shrink causing
  // the toolbar layout of button all align to left. Complete and Next button
  // should always align to right."
  //
  // Cause: the previous round pushed the MIDDLE cell right with
  // `margin-inline-start: auto`. That works only while the middle cell has
  // width — when LD re-renders after completion the mark-complete form is gone,
  // the cell collapses, and the auto-margin has nothing to push, so Next
  // slides back to the left.
  //
  // The design already solves this: `_html-template/topic-1.html:292` wraps the
  // two right-hand controls in `.bnrmp-topic__actions-group`, and
  // components/_button.scss:353 already styles it (flex row, spacing--4 gap).
  // Wrapping is stable because the GROUP holds the right edge whether or not
  // Mark-complete is inside it.
  //
  // 🔴 THE FORM IS MOVED, NOT REBUILT — same rule as the row itself. The cells
  // are reparented with appendChild, so the mark-complete form, its nonce and
  // LD's listeners travel intact. Nothing is cloned or re-created.
  const cells = [...actions.querySelectorAll('.ld-content-action')];
  // 🔴 THE MIDDLE CELL IS NOT ALWAYS A BUTTON. Matching only
  // `.learndash_mark_complete_button` missed the COMPLETED case: once a step is
  // done LD drops the form and `pharmascience_step_complete_chip()` puts a
  // `.bnrmp-step-complete` chip there instead, so the cell was never found, was
  // never grouped, and the chip sat left-aligned beside Previous while Next
  // went to the far right. Seen only in a screenshot — every numeric probe had
  // measured the un-completed state.
  const mcCell = cells.find(
    (c) => c.querySelector('.learndash_mark_complete_button, .bnrmp-step-complete')
  );
  // The LAST cell is Next (LD emits prev | mark-complete+back | next). Falling
  // back to "the last cell that is not the mark-complete cell" keeps this
  // working on the first/last topic, where LD emits an `.ld-empty` placeholder.
  const nextCell = cells.filter((c) => c !== mcCell).pop();
  if (nextCell) {
    const group = document.createElement('div');
    group.className = 'bnrmp-topic__actions-group';
    // Insert the group where the right-hand controls already are, then move the
    // live cells into it.
    nextCell.parentNode.insertBefore(group, mcCell || nextCell);
    if (mcCell) group.appendChild(mcCell);
    group.appendChild(nextCell);
  }

  const fill = bar.querySelector('.bnrmp-readprogress__fill');
  const submit = actions.querySelector('.learndash_mark_complete_button');

  // ── The scroll gate on Mark-complete ────────────────────────────────────
  //
  // Operator request 2026-08-20 (b): the button is muted/disabled until the
  // page has been scrolled, then becomes the navy Primary.
  //
  // 🔴 THIS REVERSES THE EARLIER DECISION IN THIS FILE, ON AN EXPLICIT
  // OPERATOR CALL. The previous round left the control always-operable and
  // argued a scroll gate risks WCAG failures. That risk is real, so the gate
  // ships with three release paths — each of which closes one specific way a
  // scroll-only gate strands a user. They are requirements, not polish:
  //
  //   1. NOTHING TO SCROLL = READ. `max <= 8` yields 100, so a short topic, a
  //      tall viewport or 200% zoom (WCAG 1.4.4) can never leave the button
  //      permanently locked. Without this the gate is a dead end on exactly
  //      the pages item (c) of this same request makes shorter.
  //   2. KEYBOARD / AT REACH THE END WITHOUT SCROLLING. End and PageDown
  //      release the gate, and so does focus landing inside the toolbar — a
  //      user who tabs to the control has reached it, which is the same
  //      evidence of intent a scroll to the bottom provides.
  //   3. IT IS ANNOUNCED, NOT JUST GREY. `aria-disabled` + a `title` naming
  //      the reason, so AT conveys "why" instead of an inert control.
  //
  // `aria-disabled` rather than the `disabled` PROPERTY is what makes (2)
  // possible: a real `disabled` input is removed from the tab order, so it
  // could never receive the focus that releases it. The click is suppressed in
  // a capturing listener instead.
  //
  // 🔴 THE FORM AND ITS NONCE ARE UNTOUCHED. Nothing here rewrites the input's
  // value, name or form, and the suppression is a listener — remove this
  // module and the control submits exactly as LearnDash intends.

  let unlocked = false;

  // ── Announcements (WCAG 4.1.3 Status Messages) ───────────────────────────
  //
  // One polite live region, in the DOM from first paint (see topic.php). A
  // region injected at the moment of the change is frequently not announced,
  // because the AT never observed it becoming non-empty.
  const statusRegion = bar.querySelector('.bnrmp-topicbar__status');
  const announce = (message) => {
    if (!statusRegion || !message) return;
    // Clear first: re-setting the SAME text is not a mutation, so a repeated
    // message (two muted activations in a row) would otherwise be silent.
    statusRegion.textContent = '';
    window.setTimeout(() => {
      statusRegion.textContent = message;
    }, 120);
  };

  // ── The visible instruction (WCAG 3.3.2 Labels or Instructions) ───────────
  //
  // 🔴 `title` WAS THE ONLY CARRIER AND THAT WAS A DEFECT. A title is
  // hover-only: it does not exist on touch, is unreliable on
  // `<input type=submit>`, and is invisible to exactly the iPad learners this
  // course is most used on. The reporter of #191 described "greyed out and I
  // could not select it" — the words of someone who never saw a tooltip.
  //
  // The hint is now real text next to the control, linked with
  // aria-describedby so it is part of the button's accessible description
  // rather than a floating sentence.
  const HINT_ID = 'bnrmp-topicbar-hint';
  let hintEl = null;

  const showHint = (text) => {
    if (!submit || !text) return;
    if (!hintEl) {
      hintEl = document.createElement('p');
      hintEl.className = 'bnrmp-topicbar__hint';
      hintEl.id = HINT_ID;
      const cell = submit.closest('.ld-content-action') || submit.parentNode;
      if (cell) cell.appendChild(hintEl);
    }
    hintEl.textContent = text;
    submit.setAttribute('aria-describedby', HINT_ID);
  };

  const hideHint = () => {
    if (hintEl) hintEl.remove();
    hintEl = null;
    if (submit) submit.removeAttribute('aria-describedby');
  };

  const setLocked = (locked) => {
    if (!submit) return;
    // The variant is a CLASS SWAP between two real button modifiers, so neither
    // state is described twice.
    submit.classList.toggle('bnrmp-btn--muted', locked);
    submit.classList.toggle('bnrmp-btn--primary', !locked);
    if (locked) {
      submit.setAttribute('aria-disabled', 'true');
      const why = bar.dataset.bnrmpLockedHint;
      // Kept as a convenience for pointer users who hover; it is no longer the
      // only carrier of the requirement.
      if (why) {
        submit.setAttribute('title', why);
        showHint(why);
      }
    } else {
      submit.removeAttribute('aria-disabled');
      submit.removeAttribute('title');
      hideHint();
    }
  };

  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    setLocked(false);
    // WCAG 4.1.3: someone who heard "unavailable" needs to be told it changed.
    announce(bar.dataset.bnrmpReleasedHint);
  };

  // Capturing so it runs before LearnDash's own submit handling.
  if (submit) {
    // Base component class. The state modifier is applied by setLocked() so
    // there is exactly one place that decides which variant is on.
    submit.classList.add('bnrmp-btn');
    setLocked(true);
    submit.addEventListener(
      'click',
      (event) => {
        if (submit.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopPropagation();

          // 🔴 A CANCELLED CLICK WITH NO CONSEQUENCE WAS A DEAD END FOR
          // SYNTHESISED INPUT (WCAG 2.1.1, and 2.5.1 where a scroll gesture
          // was effectively the only way through). iOS VoiceOver double-tap,
          // TalkBack, Switch Control and Dragon "click Mark complete" all
          // produce a POINTER activation, which does not match
          // `:focus-visible`, so release path 2 never fired for them and the
          // control was simply inert with no discoverable alternative.
          //
          // Now the activation does the thing the hint asks for: it moves the
          // learner to the end of the content and says so. The second
          // activation completes the step. Every input modality therefore has
          // a route that needs no knowledge of the End key.
          const target = document.querySelector('.bnrmp-topicbar__content-end');
          if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ block: 'end', behavior: 'smooth' });
            announce(bar.dataset.bnrmpJumpedHint);
          } else {
            // No sentinel to scroll to means the gate cannot be satisfied by
            // scrolling, so it must not block at all.
            unlock();
          }
          return;
        }
        // Optimistic completion styling: LD replaces this markup on the next
        // render, but the POST is in flight until then and the control must
        // not sit on Primary as though nothing happened.
        submit.classList.add('is-complete');
        renderTick();
      },
      true
    );

    // ── Release path 2 REMOVED: focus no longer silently unlocks ──────────
    //
    // 🔴 THIS WAS A 4.1.2 CONTRADICTION. The control announced
    // `aria-disabled="true"`, and then merely receiving keyboard focus removed
    // that state with no announcement — so what AT had said ("unavailable")
    // and what was true (operable) disagreed, and the learner was never told.
    // It also made the gate meaningless for keyboard users (one Tab) while
    // leaving synthesised-tap users, who cannot match `:focus-visible` at all,
    // with a completely dead control. An access gate that is easiest to bypass
    // for the people least likely to be blocked by it is the wrong shape.
    //
    // What replaces it: activating the muted control SCROLLS to the end of the
    // content and announces that (see the click handler above). Keyboard users
    // press Enter/Space and get the same route as everyone else — one
    // activation to arrive, one to complete — and the announced state is always
    // the true state. `End` also still releases.
    //
    // The `:focus-visible` discriminator is gone with it. Its own comment
    // recorded that `focusin` alone had made the gate cosmetic because pointer
    // focus fired before the click; that whole class of ordering bug does not
    // exist once focus is not a release path.
  }

  // The green circle + white tick. Injected into the button's CELL, beside the
  // control, never into the button itself — an `<input type="submit">` cannot
  // contain markup, and rewriting its `value` would change the submitted data.
  // One source for the tick glyph, used by both paths in renderTick().
  // Declared ABOVE its consumer on purpose: `const` is not hoisted the way a
  // function declaration is, so referencing it earlier in the module body would
  // throw a TDZ ReferenceError the first time the button is clicked — a failure
  // that `node --check` cannot see because it is a runtime, not a syntax, error.
  // GREEN CIRCLE, WHITE TICK — the same SVG as the dashboard's
  // `.bnrmp-modulecard__status--complete` (inc/child_dashboard_progress.php:265)
  // and the AJAX chip's buildIcon() (modules/mark-complete-ajax.js:46). Was a
  // bare polyline in currentColor, which read as a stroked outline glyph on the
  // navy completed button instead of the filled badge.
  //
  // The circle is `fill="currentColor"` (so it takes the green from
  // `.bnrmp-topicbar__tick`) and the tick is a HARDCODED #FFFFFF path. Do not
  // "unify" the tick onto currentColor — that pairing is what makes one SVG
  // correct on both the navy button and a light surface.
  const TICK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="10" fill="currentColor"></circle>' +
    '<path d="M8 12.5l2.5 2.5L16 9.5" stroke="#FFFFFF" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  function renderTick() {
    if (!submit) return;
    // Inside a <button> the tick can REPLACE the leading icon, which is what
    // the design asks for — the same slot showing "to do" then "done". LD's
    // default `<input>` cannot hold children, so there the tick is appended to
    // the surrounding cell instead. Both paths are live because the <button> is
    // produced by a PHP filter that is scoped to the toolbar: if that filter is
    // ever removed or LD's markup changes, this still degrades to the sibling.
    const icon = submit.querySelector('.bnrmp-btn__icon');
    if (icon) {
      const span = document.createElement('span');
      span.className = 'bnrmp-topicbar__tick';
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = TICK_SVG;
      icon.replaceWith(span);
      return;
    }
    const cell = submit.closest('.ld-content-action') || submit.parentNode;
    if (!cell || cell.querySelector('.bnrmp-topicbar__tick')) return;
    const tick = document.createElement('span');
    tick.className = 'bnrmp-topicbar__tick';
    tick.setAttribute('aria-hidden', 'true');
    tick.innerHTML = TICK_SVG;
    cell.appendChild(tick);
  }


  // ── Scroll-proportional fill, and the gate it drives ────────────────────

  let ticking = false;

  const update = () => {
    ticking = false;
    const doc = document.documentElement;
    // Scrollable distance. Zero on a topic shorter than the viewport — guard
    // it or the division yields NaN and the fill renders empty on a page that
    // is already fully read.
    // 🔴 MEASURE THE CONTENT, NOT THE DOCUMENT — the same thing the gate
    // measures. While the gate watched the document bottom and the fill did
    // too, they agreed. After the #191 fix moved the GATE to the content end,
    // a document-relative fill disagreed with it: measured on FR 822 at
    // vh=600, the button unlocked while its own fill read 75.55%, so the
    // learner saw an enabled control that looked three-quarters finished.
    // One definition of "read to the end", used by both.
    const endEl =
      document.querySelector('.bnrmp-topicbar__content-end') ||
      document.querySelector('.ld-tabs-content') ||
      document.querySelector('.learndash-wrapper');
    const barPx = (Math.ceil(bar.getBoundingClientRect().height) || 0) + 24;
    const max = endEl
      ? Math.max(
          0,
          Math.round(
            endEl.getBoundingClientRect().bottom +
              window.scrollY -
              (window.innerHeight - barPx)
          )
        )
      : doc.scrollHeight - window.innerHeight;
    // 🔴 RELEASE PATH 1. A page with nothing to scroll is fully read by
    // definition, so it reads 100 rather than 0. Without this branch the gate
    // never opens on a short topic — and item (c) of this same request makes
    // short topics MORE common by stretching the shell to the viewport.
    const pct = max > 8 ? (window.scrollY / max) * 100 : 100;
    // Clamped: rubber-band scrolling reports negative scrollY and overscroll
    // past the end, both of which push the fill outside its track.
    const clamped = Math.max(0, Math.min(100, pct));
    const value = clamped.toFixed(2) + '%';

    if (fill) fill.style.setProperty('--bnrmp-readprogress', value);
    if (submit) {
      // The FILL stays proportional to the whole document — it answers "how far
      // down the page am I", which is a visual ratio and correct as-is.
      submit.style.setProperty('--bnrmp-readprogress', value);
    }
  };

  // ── RELEASE PATH 4 — the END OF THE CONTENT is in view ───────────────────
  //
  // 🔴 THIS REPLACES A DOCUMENT-BOTTOM PERCENTAGE GATE THAT COULD NOT BE
  // SATISFIED BY READING. BugHerd #191: a learner read every word of FR topic
  // 822 in a non-maximized window, the button stayed muted, and maximizing
  // "fixed" it. Both halves were the same arithmetic bug.
  //
  // The old condition was `scrollY / (scrollHeight - innerHeight) > 99%`, i.e.
  // the bottom of the DOCUMENT — which includes the 80px site footer and ~158px
  // of shell padding BELOW the last line of learning content. Measured on 822:
  //
  //   content ends at   945px      document height   1183px
  //   -> 238px of the scrollable distance is footer + padding, and on this
  //      topic the ENTIRE scrollable distance is only 283px.
  //
  //   viewport   scroll max   % reached at end of CONTENT   gate
  //   >=1183     0            100 (release path 1)          unlocks on load
  //    900       283          40.3                          LOCKED
  //    768       415          59.3                          LOCKED
  //    420       763          77.9                          LOCKED
  //
  // So reading to the end of the content reached 40-78% and never 99%, while a
  // window taller than the document unlocked instantly with no scrolling at
  // all. The gate was measuring the footer, not the reading.
  //
  // An IntersectionObserver on a sentinel at the end of the CONTENT is the fix
  // rather than a bigger pixel tolerance, because:
  //   - it asks the question we actually mean ("has the learner reached the end
  //     of the material") instead of a proxy that the footer's height poisons;
  //   - it fires on LAYOUT change, not just scroll, so a late-loading image or
  //     an expanding accordion re-evaluates with no scroll event;
  //   - it is computed in layout space, so pinch-zoom and iOS Safari's
  //     collapsing URL bar (which move the VISUAL viewport only, and which
  //     `innerHeight` arithmetic reads inconsistently) cannot strand it;
  //   - `rootMargin` expresses "clear of the fixed toolbar" declaratively,
  //     with no magic pixel arithmetic against innerHeight.
  //
  // `unlock()` stays the single owner of the state — idempotent and monotonic.
  // The observer is just another CALLER, exactly like keydown and focusin.
  const contentEnd = () => {
    // The LD content wrapper ends where the material ends (measured: 945px on
    // topic 822, vs the footer starting at 1041px).
    //
    // 🔴 NO FALLBACK TO `actions`. An earlier version of this function ended
    // `|| actions`, described as degrading to "unlock when the actions row is
    // reached". The measurable behaviour was the OPPOSITE of that comment:
    // `actions` has already been moved INTO the fixed bar (line ~28), so the
    // sentinel would land inside the bar at y > innerHeight - barHeight, while
    // the trigger line sits at innerHeight - barHeight - CLEARANCE. It could
    // never intersect. A LearnDash rename of `.ld-tabs-content` would then
    // have locked EVERY topic for every mouse and touch user at once.
    //
    // Returning null instead routes to the same fail-open branch as a missing
    // IntersectionObserver: on required regulated training, a gate that cannot
    // open must release, never trap.
    return (
      document.querySelector('.ld-tabs-content') ||
      document.querySelector('.learndash-wrapper') ||
      null
    );
  };

  const observeContentEnd = () => {
    try {
      observeContentEndUnsafe();
    } catch (e) {
      // Anything unexpected between setLocked(true) and a live observer would
      // otherwise leave the button muted with the click-suppressor attached
      // and no release path — a locked door with the key thrown away.
      unlock();
    }
  };

  const observeContentEndUnsafe = () => {
    if (typeof window.IntersectionObserver !== 'function') {
      // No observer (very old engine): do NOT leave a gate that cannot open.
      // Release immediately — the scroll fill still shows reading progress and
      // the quiz remains the real completion gate.
      unlock();
      return;
    }

    const container = contentEnd();
    if (!container) {
      unlock();
      return;
    }

    // 🔴 OBSERVE A ZERO-HEIGHT SENTINEL AT THE END, NOT THE CONTAINER ITSELF.
    // The container spans the whole topic (measured: 21121px on topic 818), so
    // it intersects the viewport from the moment the page loads and
    // `isIntersecting` would be true before the learner has read anything —
    // exactly the "cosmetic gate" failure this file already recorded once.
    // A 0px marker appended after the last content node intersects only when
    // the END of the material is actually on screen.
    const target = document.createElement('div');
    target.className = 'bnrmp-topicbar__content-end';
    target.setAttribute('aria-hidden', 'true');
    // 🔴 1px TALL, NOT 0. A zero-height box landing EXACTLY on the rootMargin
    // trigger line does not intersect (measured on topic 818: sentinel at
    // viewport y=831 against a trigger line of 831 -> isIntersecting false),
    // so a learner who stopped one pixel short of that line stayed locked.
    // 1px removes the boundary case. It is pulled back out of flow with a
    // negative margin so it still adds no scrollable distance of its own.
    target.style.cssText =
      'height:1px;margin:0 0 -1px 0;padding:0;border:0;pointer-events:none;';
    container.appendChild(target);

    // Bottom margin pulls the trigger line UP by the toolbar's height, so the
    // sentinel counts as "seen" only once it clears the fixed bar rather than
    // while it is hidden behind it.
    // Pull the line up by the bar plus one line of text, so the last sentence
    // is genuinely READABLE above the toolbar rather than flush against it.
    // A knife-edge margin makes the gate depend on sub-pixel layout, which is
    // how #191's percentage version failed in the first place.
    const CLEARANCE_PX = 24;

    // The bar's height EXCLUDING the locked-state hint.
    //
    // 🔴 MEASURED, NOT ASSUMED. An earlier version of this helper measured
    // `.bnrmp-topicbar__inner` believing the hint sat outside it. It does not:
    // locked = bar 107px / inner 106px, released = bar 69px / inner 68px, so
    // BOTH swing by the same 38px and measuring `inner` decoupled nothing.
    //
    // Subtracting the hint's own box is what actually removes the circular
    // dependency — the gate's trigger line must not move because of the
    // element that explains the gate. Floor of 44px (the design's minimum hit
    // area) so a mid-reflow measurement of 0 cannot turn the whole viewport
    // into the trigger line, which would be a permanent lock.
    const stableBarHeight = () => {
      const total = Math.ceil(bar.getBoundingClientRect().height) || 0;
      const hintBox = hintEl ? Math.ceil(hintEl.getBoundingClientRect().height) : 0;
      // The hint's top margin travels with it; read the used value rather than
      // hardcoding the token.
      const hintGap = hintEl
        ? Math.ceil(parseFloat(window.getComputedStyle(hintEl).marginTop) || 0)
        : 0;
      // ponytail: clamp at 0. Since 2026-09-14 the hint is sr-only (clip
      // pattern, margin:-1px), so it contributes NO layout and hintBox+hintGap
      // measures -1 — which would ADD a pixel to the bar height. Stale-too-LARGE
      // is the dangerous direction per the note below, so never let the
      // subtraction go negative. Re-styling the hint visible again just works.
      return Math.max(44, total - Math.max(0, hintBox + hintGap));
    };

    // 🔴 RE-MEASURE ON RESIZE. `rootMargin` is fixed at construction, so a
    // single measurement goes stale on rotation, zoom-after-load, or the bar
    // wrapping to two rows on a narrow viewport. Stale-too-small only mis-fires
    // cosmetically; stale-too-large can leave the effective root height at or
    // below zero, which is a PERMANENT LOCK for anyone without a keyboard
    // (measured worst case: iPhone SE landscape at 200% text, viewport ~250px,
    // bar wrapped to ~130px). Rebuilding the observer is cheap and only ever
    // happens while the gate is still locked — `unlock()` disconnects it.
    let io = null;
    const rebuild = () => {
      if (unlocked) return;
      if (io) io.disconnect();
      io = build();
      io.observe(target);
    };
    const build = () => new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // isIntersecting alone is not enough: on a page shorter than the
          // viewport the end of the content is visible from the start, which
          // is correct (nothing to scroll = read, release path 1's premise).
          if (entry.isIntersecting) {
            unlock();
            io.disconnect();
          }
        });
      },
      {
        root: null,
        // 🔴 DERIVED FROM THE BAR'S *RELEASED* HEIGHT, NOT ITS CURRENT ONE.
        //
        // The locked-state hint lives beside the button INSIDE this bar, so
        // showing it makes the bar taller — measured 107px with the hint vs
        // 69px without. Feeding the current height into `rootMargin` therefore
        // created a circular dependency: the gate's trigger line moved because
        // of the very element that describes the gate, and the extra 38px of
        // trigger line was enough to push release back to the document bottom
        // on 4 of 6 measured topic/viewport combinations — reintroducing #191.
        //
        // `__inner` is the row that holds the controls and does NOT contain the
        // hint, so its height is stable across locked and released states.
        // Clamped to a floor so a mid-reflow measurement of 0 cannot make the
        // trigger line the whole viewport.
        rootMargin:
          '0px 0px -' + (stableBarHeight() + CLEARANCE_PX) + 'px 0px',
        threshold: 0,
      }
    );

    rebuild();
    window.addEventListener('resize', rebuild, { passive: true });

    // 🔴 THE BAR'S OWN HEIGHT IS AN INPUT TO `rootMargin`, AND THE VISIBLE
    // LOCKED-STATE HINT CHANGES IT. Measured: 107px while the hint is shown,
    // 69px once it is removed on release — a 38px swing in the trigger line
    // that a `resize`-only rebuild never sees, because the window never
    // resized. Anything that reflows the bar (the hint appearing, a label
    // wrapping to two lines, a font swap, the tick replacing the icon) has the
    // same effect.
    //
    // ResizeObserver watches the element instead of the window, which is the
    // only thing that actually tracks this. Guarded: the API is absent on no
    // browser this fleet runs, but a missing one must degrade to the
    // resize-only behaviour, never to a gate that cannot open.
    if (typeof window.ResizeObserver === 'function') {
      const ro = new window.ResizeObserver(() => {
        if (unlocked) {
          ro.disconnect();
          return;
        }
        rebuild();
      });
      ro.observe(bar);
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  // Resize and late-loading images both change scrollHeight, so the ratio goes
  // stale without these.
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('load', onScroll);

  // Release path 3 (keyboard) — End jumps to the end of the document without
  // necessarily firing a scroll event that reaches the sentinel.
  //
  // 🔴 `PageDown` WAS REMOVED HERE. It released the gate from ANY position,
  // including the very top: measured on topic 818 (21359px), ONE PageDown
  // opened the gate with 96% of the page unread. That made the gate both
  // trivially bypassable for anyone who knew the key AND still blocking for
  // the learner who did not — the worst of both. `End` is kept because it
  // genuinely goes to the end of the document; the observer above then sees
  // the sentinel on the resulting scroll anyway, so this path is now a
  // belt-and-braces for engines where the observer callback is delayed.
  window.addEventListener('keydown', (event) => {
    if (event.key === 'End') unlock();
  });

  // 🔴 WIRE THE RELEASES BEFORE ANY FURTHER WORK CAN THROW. setLocked(true)
  // ran far above; if an exception happened between there and here the button
  // would sit muted with the click-suppressor attached and NO release path —
  // locked door, discarded key. Everything that can release the gate is now
  // registered by this line.
  observeContentEnd();

  update();
}
